import { Role } from 'src/app/models/role'

export type SettingsTileId =
  | 'customise-data'
  | 'custom-products'
  | 'custom-reports'
  | 'elevations'
  | 'report-information'
  | 'users'
  | 'job-schedules'
  | 'scan-to-import'

export interface SettingsAccess {
  hasRole: (role: string) => boolean
  profileType?: string
  /** Native app (or a non-production web build, which stands in for one) */
  isMobile: boolean
}

export interface SettingsTile {
  id: SettingsTileId
  title: string
  description: string
  /** FontAwesome regular icon name — must be registered in app.module.ts */
  icon: string
  link: string
  canSee: (access: SettingsAccess) => boolean
}

export interface SettingsGroup {
  title: string
  tiles: SettingsTile[]
}

/**
 * The Settings page's tiles. Each keeps the permission its old side-menu entry had,
 * minus the online check: offline, tiles stay visible but paused.
 */
export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    title: 'Data and templates',
    tiles: [
      {
        id: 'customise-data',
        title: 'Customise data',
        description: 'Tailor the options, templates and categories your team picks from.',
        icon: 'sliders',
        link: '/app/customise-data-v2',
        canSee: (a) => a.hasRole(Role.Options),
      },
      {
        id: 'custom-products',
        title: 'Product builder',
        description: 'Design templates for assets beyond fire doors.',
        icon: 'cubes',
        link: '/app/custom-products',
        canSee: (a) => a.hasRole(Role.Admin),
      },
      {
        id: 'custom-reports',
        title: 'Report builder',
        description: 'Build your own inspection and assessment report templates.',
        icon: 'file-lines',
        link: '/app/custom-reporting',
        canSee: (a) => a.hasRole(Role.Admin) || a.hasRole(Role.CreateEditReports),
      },
      {
        id: 'elevations',
        title: 'Elevations',
        description: 'Set the door layouts your team chooses from when adding fire doors.',
        icon: 'door-open',
        link: '/app/firedoor-elevations',
        canSee: (a) => a.hasRole(Role.Elevations),
      },
    ],
  },
  {
    title: 'Reports',
    tiles: [
      {
        id: 'report-information',
        title: 'Report information',
        description: 'Set the standard wording printed on your inspection reports.',
        icon: 'file-signature',
        link: '/app/settings/report-information',
        canSee: (a) => a.hasRole(Role.EntityAdmin) && a.profileType !== 'Manufacturer',
      },
    ],
  },
  {
    title: 'Team and work',
    tiles: [
      {
        id: 'users',
        title: 'Users',
        description: 'Add team members and manage their access and licences.',
        icon: 'user-group',
        link: '/app/users',
        canSee: (a) => a.hasRole(Role.UserAdmin),
      },
      {
        id: 'job-schedules',
        title: 'Job schedule settings',
        description: 'Choose who receives job schedules other companies share with you.',
        icon: 'calendar-check',
        link: '/app/settings/job-schedules',
        canSee: (a) => a.hasRole(Role.EntityAdmin) && a.hasRole(Role.JobSchedules),
      },
    ],
  },
  {
    title: 'Tools',
    tiles: [
      {
        id: 'scan-to-import',
        title: 'Scan to import',
        description: 'Scan a FireDNA tag or QR code to bring a product into your account.',
        icon: 'qrcode',
        link: '/app/import-subcollection',
        canSee: (a) => a.hasRole(Role.Import) && a.isMobile,
      },
    ],
  },
]

export function visibleSettingsGroups(access: SettingsAccess): SettingsGroup[] {
  return SETTINGS_GROUPS.map((group) => ({ ...group, tiles: group.tiles.filter((tile) => tile.canSee(access)) })).filter((group) => group.tiles.length > 0)
}

export function hasAnySettings(access: SettingsAccess): boolean {
  return SETTINGS_GROUPS.some((group) => group.tiles.some((tile) => tile.canSee(access)))
}

/** Routes the Settings menu entry stays highlighted on */
export const SETTINGS_ROUTES: string[] = ['/app/settings', ...SETTINGS_GROUPS.flatMap((group) => group.tiles.map((tile) => tile.link))]
